import React, { useState, useMemo, useEffect } from "react";
import "./Login.scss";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";
import { SdsForm } from "soft_digi";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { post } from "../../services/smartApiService";
import { useNavigate } from "react-router-dom";
import { showAlertAutoClose } from "../../services/notifyService";

// Props for each sub-component
type RegisterPageProps = {
  setStage: (val: string) => void;
  onSubmit: (data: any) => void;
};
interface HeaderProps {
  setIsRegistering: (data: any) => void;
};

const ModalLoginPage: React.FC<HeaderProps> = ({ setIsRegistering }) => {
  const [stage, setStage] = useState("FIRST");
  const [otpId, setOtpId] = useState<any>();
  const [userData, setUserData] = useState<any>();
  const navigate = useNavigate();

  // Reset to first stage when component mounts
  useEffect(() => {
    setStage("FIRST");
  }, []);

  // Validation Schemas
  const validationSchema = useMemo(() => {
    if (stage === "FIRST") {
      return Yup.object({
        emailid: Yup.string()
          .email("Please enter a valid email address")
          .required("Email is required")
          .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            "Invalid email format"
          ),
      });
    }

    if (stage === "SECOND") {
      return Yup.object({
        otp_value: Yup.string()
          .required("OTP is required")
          .length(6, "OTP must be exactly 6 digits")
          .matches(/^[0-9]+$/, "OTP must contain only numbers"),
      });
    }

    if (stage === "THIRD") {
      return Yup.object({
        mobile_no: Yup.string()
          .required("Mobile number is required")
          .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
        ename: Yup.string()
          .required("Full name is required")
          .min(2, "Name must be at least 2 characters")
          .max(50, "Name cannot exceed 50 characters"),
        epassword: Yup.string()
          .required("Password is required"),
          // .min(8, "Password must be at least 8 characters")
          // .matches(
          //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          //   "Password must contain at least one uppercase, one lowercase, one number and one special character"
          // ),
        confirm_password: Yup.string()
          .oneOf([Yup.ref("epassword")], "Passwords must match")
          .required("Please confirm your password"),
      });
    }

    return Yup.object({});
  }, [stage]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(validationSchema) , });

  const onSubmit = (data: any) => {
    setUserData({ ...userData, ...data });

    if (stage === "FIRST") {
      const url = "/auth/send-otp";
      const emailid = data.emailid;

      const subscription = post(
        url,
        { emailid },
        { requiresAuthorization: false }
      ).subscribe({
        next: (response) => {
          setOtpId(response.data.otp_id);
          showAlertAutoClose("OTP sent successfully to your email");
          setStage("SECOND");
        },
        error: (err) => {
          showAlertAutoClose("Failed to send OTP. Please try again.");
        }
      });

      return () => subscription.unsubscribe();
    }

    if (stage === "SECOND") {
      const url = "/auth/verify_otp";
      const _data = {
        otp: data.otp_value,
        emailid: userData.emailid,
      };

      const subscription = post(url, _data, {
        requiresAuthorization: false,
      }).subscribe({
        next: (response) => {
          showAlertAutoClose("OTP verified successfully");
          setStage("THIRD");
          reset(); // Clear OTP field
        },
        error: (err) => {
          showAlertAutoClose("Invalid OTP. Please try again.");
        }
      });

      return () => subscription.unsubscribe();
    }

    if (stage === "THIRD") {
      const url = "auth/signup";
      const _data = {
        emailid: userData.emailid,
        epassword: data.epassword,
        mobile_no: data.mobile_no,
        ename: data.ename,
      };

      const subscription = post(url, _data, {
        requiresAuthorization: false,
      }).subscribe({
        next: (response) => {
          showAlertAutoClose("Registration successful! You can now login.");
          setStage("FIRST");
          setIsRegistering("LOGIN");
          reset();
        },
        error: (err) => {
          showAlertAutoClose("Registration failed. Please try again.");
        }
      });

      return () => subscription.unsubscribe();
    }
  };

  // Stage TWO form (OTP verification)
  const RegisterPage: React.FC<RegisterPageProps> = ({ onSubmit }) => {
    const stage2_elements: SdsFormElementProps[] = [
      {
        type: "TEXT_BOX",
        width: 12,
        name: "otp_value",
        element: {
          label: "Enter OTP",
          placeHolder: "6-digit OTP received in your email",
          max: 6,
          isRequired: true,
          inputType: "BORDER_LABEL",
        },
      },
      {
        type: "BUTTON",
        width: 12,
        name: "verify_otp",
        class_name: "login-button",
        element: {
          label: "Verify OTP",
          onClick: handleSubmit(onSubmit),
          classList: ["smart-login-button"],
        },
      },
    ];
    return <SdsForm elements={stage2_elements} errors={errors} control={control} />;
  };

  // Stage ONE form (Email Registration)
  const Registrations: React.FC<RegisterPageProps> = ({ onSubmit }) => {
    const registrationElements: SdsFormElementProps[] = [
      {
        type: "TEXT_BOX",
        width: 12,
        name: "emailid",
        element: {
          label: "Email ID",
          placeHolder: "Enter your email address",
          isRequired: true,
          inputType: "BORDER_LABEL",
        },
      },
      {
        type: "BUTTON",
        width: 12,
        name: "register",
        class_name: "login-button",
        element: {
          label: "Send OTP",
          onClick: handleSubmit(onSubmit),
          classList: ["smart-login-button"],
        },
      },
    ];
    return <SdsForm elements={registrationElements} errors={errors} control={control} />;
  };

  // Stage THREE form (Final Registration Details)
  const Registration_Last: React.FC<RegisterPageProps> = ({ onSubmit }) => {
    const elements: SdsFormElementProps[] = [
      {
        type: "PASSWORD",
        width: 12,
        name: "epassword",
        element: {
          label: "Password",
          placeHolder: "Create a strong password",
          isRequired: true,
          passwordValidator: true,
          inputType: "BORDER_LABEL",
    
        },
      },
      {
        type: "PASSWORD",
        width: 12,
        name: "confirm_password",
        element: {
          label: "Confirm Password",
          placeHolder: "Re-enter your password",
          isRequired: true,
          passwordValidator: false,
          inputType: "BORDER_LABEL",
        },
      },
      {
        type: "TEXT_BOX",
        width: 12,
        name: "mobile_no",
        element: {
          label: "Mobile Number",
          placeHolder: "Enter 10-digit mobile number",
          isRequired: true,
          inputType: "BORDER_LABEL",
          max: 10,
        },
      },
      {
        type: "TEXT_BOX",
        width: 12,
        name: "ename",
        element: {
          label: "Full Name",
          placeHolder: "Enter your full name",
          isRequired: true,
          inputType: "BORDER_LABEL",
        },
      },
      {
        type: "BUTTON",
        width: 12,
        name: "complete_registration",
        class_name: "login-button",
        element: {
          label: "Complete Registration",
          onClick: handleSubmit(onSubmit),
          classList: ["cta-button has-text-right"],
        },
      },
    ];
    return <SdsForm elements={elements} errors={errors} control={control} />;
  };

  return (
    <div className="registration-modal">
      {stage === "FIRST" && (
        <Registrations setStage={setStage} onSubmit={onSubmit} />
      )}
      {stage === "SECOND" && (
        <RegisterPage setStage={setStage} onSubmit={onSubmit} />
      )}
      {stage === "THIRD" && (
        <Registration_Last setStage={setStage} onSubmit={onSubmit} />
      )}
    </div>
  );
};

export default ModalLoginPage;